import { useCallback, useEffect, useRef } from 'react';

/**
 * usePhotoSwipe — version Pointer Events (universel mobile + desktop).
 * - Capture pointerdown/move/up/cancel sur le ref → robuste sur tous OS/navigateurs
 * - Détection au pointerup uniquement (pas de follow-finger qui causait les bugs)
 * - Seuil 40px, gestes diagonaux ignorés, vertical → laisse passer pour scroll
 */
export function usePhotoSwipe({ isOpen, onClose, onPrev, onNext, totalPhotos, currentIndex }) {
  const photoRef = useRef(null);
  const inputFocused = useRef(false);

  // Refs pour valeurs courantes (évite recréation des listeners)
  const stateRef = useRef({});
  stateRef.current = { currentIndex, totalPhotos, onNext, onPrev };

  const goToNext = useCallback(() => {
    const s = stateRef.current;
    if (s.currentIndex >= s.totalPhotos - 1) return;
    s.onNext?.();
  }, []);

  const goToPrev = useCallback(() => {
    const s = stateRef.current;
    if (s.currentIndex <= 0) return;
    s.onPrev?.();
  }, []);

  // Pointer Events (touch + mouse + stylet) — universel
  // ⚠️ dépendance à `isOpen` indispensable : le ref n'existe que quand le viewer est monté
  useEffect(() => {
    if (!isOpen) return;
    const el = photoRef.current;
    if (!el) return;

    const g = { x0: 0, y0: 0, active: false, decided: false, horizontal: false, pointerId: null };

    const onDown = (e) => {
      if (inputFocused.current) return;
      // Ignorer si focus sur un champ texte (textarea de message, etc.)
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      g.x0 = e.clientX;
      g.y0 = e.clientY;
      g.active = true;
      g.decided = false;
      g.horizontal = false;
      g.pointerId = e.pointerId;
      // Capture pour garantir que move/up arrivent même si le doigt sort de l'élément
      try { el.setPointerCapture && el.setPointerCapture(e.pointerId); } catch {}
    };

    const onMove = (e) => {
      if (!g.active || e.pointerId !== g.pointerId) return;
      const dx = e.clientX - g.x0;
      const dy = e.clientY - g.y0;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      if (!g.decided && (adx > 10 || ady > 10)) {
        g.decided = true;
        g.horizontal = adx > ady;
      }
    };

    const onUp = (e) => {
      if (!g.active || e.pointerId !== g.pointerId) return;
      const dx = e.clientX - g.x0;
      const adx = Math.abs(dx);
      const wasHorizontal = g.horizontal;
      g.active = false;
      g.pointerId = null;
      if (!wasHorizontal) return;
      if (adx < 40) return;
      if (dx < 0) goToNext();
      else goToPrev();
    };

    const onCancel = () => {
      g.active = false;
      g.pointerId = null;
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
    el.addEventListener('pointerleave', onCancel);

    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('pointerleave', onCancel);
    };
  }, [isOpen, goToNext, goToPrev]);

  // Clavier (← → Esc)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (inputFocused.current) return;
      if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
      else if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, goToNext, goToPrev, onClose]);

  const handleMouseMove = useCallback(() => {}, []);

  return {
    photoRef, showArrows: false, handleMouseMove,
    goToNext, goToPrev, inputFocused,
    swipeDirection: null,
    enterDir: '',
  };
}
